import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { createApiUrlWithPrefix } from '../config/api';
import { 
  Calendar,
  Clock,
  Users,
  Plus,
  Edit,
  Trash2,
  XCircle,
  UserPlus,
  CheckCircle,
  Check
} from 'lucide-react';
import CalendarView from './CalendarView';

import { API_BASE_URL } from '../config/api';
function SimplifiedSessionManagement() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [viewMode, setViewMode] = useState('weekly'); // 'weekly', 'daily', or 'calendar'
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [targetFilter, setTargetFilter] = useState('');
  const [stableFilter, setStableFilter] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    stable: '',
    target: '',
    level: '',
    type: '',
    weeklyDays: [],
    date: '',
    startTime: '',
    endTime: '',
    maxParticipants: 10,
    selectedHorses: []
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [showHorsesModal, setShowHorsesModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [horses, setHorses] = useState([]);
  const [sessionHorses, setSessionHorses] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

    const [refreshKey, setRefreshKey] = useState(0);
const stables = [
    { value: '1', label: t('sessions.simplified.form.stable1') },
    { value: '2', label: t('sessions.simplified.form.stable2') },
    { value: '3', label: t('sessions.simplified.form.stable3') }
  ];

  const sessionTargets = [
    { value: 'Rider', label: t('sessions.simplified.form.riders') },
    { value: 'Owner', label: t('sessions.simplified.form.owners') }
  ];

  const riderLevels = [
    { value: 'Beginner', label: t('sessions.simplified.form.beginner') },
    { value: 'Intermediate', label: t('sessions.simplified.form.intermediate') },
    { value: 'Professional', label: t('sessions.simplified.form.professional') }
  ];

  const sessionTypes = [
    { value: 'Weekly', label: t('sessions.simplified.form.weekly') },
    { value: 'Daily', label: t('sessions.simplified.form.daily') }
  ];

  const weeklyDays = [
    { value: 'Monday', label: t('sessions.daysOfWeek.monday') },
    { value: 'Tuesday', label: t('sessions.daysOfWeek.tuesday') },
    { value: 'Wednesday', label: t('sessions.daysOfWeek.wednesday') },
    { value: 'Thursday', label: t('sessions.daysOfWeek.thursday') },
    { value: 'Friday', label: t('sessions.daysOfWeek.friday') },
    { value: 'Saturday', label: t('sessions.daysOfWeek.saturday') },
    { value: 'Sunday', label: t('sessions.daysOfWeek.sunday') }
  ];

  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString()
      });
      
      if (searchTerm) params.append('search', searchTerm);
      if (typeFilter) params.append('type', typeFilter);
      if (targetFilter) params.append('target', targetFilter);
      if (stableFilter) params.append('stable', stableFilter);
      
      const response = await axios.get(createApiUrlWithPrefix(`simplifiedsessions?${params}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Extract sessions from the paginated response
      const sessionsData = response.data.sessions || response.data;
      
      // Filter sessions based on view mode
      const filteredSessions = sessionsData.filter(session => {
        if (viewMode === 'daily') {
          return session.type === 1; // Daily = 1
        } else {
          return session.type === 0; // Weekly = 0
        }
      });
      
      setSessions(filteredSessions);
      setTotalPages(response.data.totalPages || 1);
      setTotalItems(response.data.totalItems || filteredSessions.length);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }, [viewMode, currentPage, itemsPerPage, searchTerm, typeFilter, targetFilter, stableFilter]);

  const fetchHorses = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/horses`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      // Handle paginated response - extract horses array from response.data.horses
      const horsesData = response.data?.horses || response.data || [];
      const horsesArray = Array.isArray(horsesData) ? horsesData : [];
      setHorses(horsesArray);
      console.log('Fetched horses:', horsesArray.length, 'horses');
    } catch (error) {
      console.error('Failed to fetch horses:', error);
      toast.error('Failed to load horses');
      // Ensure we set an empty array on error
      setHorses([]);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
    fetchHorses();
  }, [fetchSessions, fetchHorses]);

  const handleCreateSession = async () => {
    if (!formData.name || !formData.stable || !formData.target || !formData.startTime || !formData.endTime) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.target === 'Rider' && !formData.level) {
      toast.error('Please select a rider level');
      return;
    }

    if (formData.type === 'Weekly' && (formData.weeklyDays || []).length === 0) {
      toast.error('Please select at least one day for weekly sessions');
      return;
    }
    
    if (formData.type === 'Daily' && !formData.date) {
      toast.error('Please select a date for daily sessions');
      return;
    }
    
    try {
      // For weekly sessions, create one session with all selected days
      if (formData.type === 'Weekly') {
        console.log('Creating weekly session for days:', formData.weeklyDays);
        
        // Get current user ID with validation
        const currentUserId = localStorage.getItem('userId');
        if (!currentUserId) {
          toast.error('User session expired. Please login again.');
          return;
        }
        
        // Create one session with the first selected day as the primary day
        const primaryDay = (formData.weeklyDays || [])[0];
          const sessionData = {
          name: formData.name,
          stable: formData.stable,
          date: getNextDateForDay(primaryDay), // Get next occurrence of the first day
            startTime: formData.startTime + ':00',
            endTime: formData.endTime + ':00',
          level: formData.target === 'Rider' ? getLevelValue(formData.level) : 0, // Default for owners
          type: getTypeValue(formData.type),
          target: getTargetValue(formData.target),
          maxParticipants: formData.maxParticipants,
          weeklyDay: getDayValue(primaryDay), // Store the first day as primary
          weeklyDays: (formData.weeklyDays || []).join(','), // Store all days as comma-separated string
          createdBy: parseInt(currentUserId), // Use actual logged-in user ID
          expiresAt: null,
          horseIds: formData.selectedHorses || []
        };

        await axios.post(`${API_BASE_URL}/api/simplifiedsessions`, sessionData, {
            headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });
        console.log('Successfully created weekly session for days:', formData.weeklyDays);
      } else {
        // For daily sessions, create one session
        // Get current user ID with validation
        const currentUserId = localStorage.getItem('userId');
        if (!currentUserId) {
          toast.error('User session expired. Please login again.');
          return;
        }

        const sessionData = {
          name: formData.name,
          stable: formData.stable,
          date: new Date(formData.date).toISOString(),
          startTime: formData.startTime + ':00',
          endTime: formData.endTime + ':00',
          level: formData.target === 'Rider' ? getLevelValue(formData.level) : 0,
          type: getTypeValue(formData.type),
          target: getTargetValue(formData.target),
          maxParticipants: formData.maxParticipants,
          weeklyDay: null, // Daily sessions don't have a specific day
          createdBy: parseInt(currentUserId), // Use actual logged-in user ID
          expiresAt: new Date(formData.date).toISOString(),
          horseIds: formData.selectedHorses || []
        };

        await axios.post(`${API_BASE_URL}/api/simplifiedsessions`, sessionData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
      }
      
      toast.success('Session(s) created successfully!');
      setShowCreateModal(false);
      resetForm();
      fetchSessions();
      setRefreshKey(prev => prev + 1); // Force component refresh
      } catch (error) {
      console.error('Error creating session:', error);
      toast.error('Failed to create session');
    }
  };

  const handleEditSession = async () => {
    if (!formData.name || !formData.stable || !formData.target || !formData.startTime || !formData.endTime) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.target === 'Rider' && !formData.level) {
      toast.error('Please select a rider level');
      return;
    }

    if (formData.type === 'Weekly' && (formData.weeklyDays || []).length === 0) {
      toast.error('Please select at least one day for weekly sessions');
      return;
    }
    
    if (formData.type === 'Daily' && !formData.date) {
      toast.error('Please select a date for daily sessions');
      return;
    }
    
    try {
      const updateData = {
        name: formData.name,
        stable: formData.stable,
        date: formData.type === 'Daily' ? new Date(formData.date).toISOString() : getNextDateForDay((formData.weeklyDays || [])[0]),
        startTime: formData.startTime + ':00',
        endTime: formData.endTime + ':00',
        level: formData.target === 'Rider' ? getLevelValue(formData.level) : 0,
        type: getTypeValue(formData.type),
        target: getTargetValue(formData.target),
        maxParticipants: formData.maxParticipants,
        weeklyDay: formData.type === 'Weekly' ? getDayValue((formData.weeklyDays || [])[0]) : null,
        weeklyDays: formData.type === 'Weekly' ? (formData.weeklyDays || []).join(',') : null,
        expiresAt: formData.type === 'Daily' ? new Date(formData.date).toISOString() : null
      };

      await axios.put(createApiUrlWithPrefix(`simplifiedsessions/${editingSession.id}`), updateData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      toast.success('Session updated successfully!');
      setShowCreateModal(false);
      resetForm();
      fetchSessions();
        setRefreshKey(prev => prev + 1); // Force component refresh
      } catch (error) {
      console.error('Error updating session:', error);
      toast.error('Failed to update session');
    }
  };

  // Helper function to get next date for a specific day of the week
  const getNextDateForDay = (dayName) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const targetDay = days.indexOf(dayName);
    const today = new Date();
    const currentDay = today.getDay();
    
    let daysUntilTarget = (targetDay - currentDay + 7) % 7;
    if (daysUntilTarget === 0) daysUntilTarget = 7; // Next week if today is the target day
    
    const nextDate = new Date(today);
    nextDate.setDate(today.getDate() + daysUntilTarget);
    return nextDate.toISOString();
  };

  // Calendar navigation functions
  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const navigateDay = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Helper function to convert level string to integer
  const getLevelValue = (level) => {
    switch (level) {
      case 'Beginner': return 0;
      case 'Intermediate': return 1;
      case 'Professional': return 2;
      default: return 0;
    }
  };

  // Helper function to convert type string to integer
  const getTypeValue = (type) => {
    switch (type) {
      case 'Weekly': return 0;
      case 'Daily': return 1;
      default: return 0;
    }
  };

  // Helper function to convert target string to integer
  const getTargetValue = (target) => {
    switch (target) {
      case 'Rider': return 0;
      case 'Owner': return 1;
      default: return 0;
    }
  };

  // Helper function to convert day string to integer
  const getDayValue = (day) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days.indexOf(day);
  };

  // Helper functions to convert integers back to strings for editing
  const getLevelName = (level) => {
    switch (level) {
      case 0: return 'Beginner';
      case 1: return 'Intermediate';
      case 2: return 'Professional';
      default: return 'Beginner';
    }
  };

  const getTypeName = (type) => {
    switch (type) {
      case 0: return 'Weekly';
      case 1: return 'Daily';
      default: return 'Weekly';
    }
  };

  const getTargetName = (target) => {
    switch (target) {
      case 0: return 'Rider';
      case 1: return 'Owner';
      default: return 'Rider';
    }
  };

  const getDayName = (dayValue) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayValue] || 'Monday';
  };


  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this session?')) {
      return;
    }

    try {
      await axios.delete(createApiUrlWithPrefix(`simplifiedsessions/${sessionId}`), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
        toast.success('Session deleted successfully!');
        fetchSessions();
        setRefreshKey(prev => prev + 1); // Force component refresh
      } catch (error) {
      console.error('Error deleting session:', error);
        toast.error('Failed to delete session');
      }
  };

  const handleEdit = (session) => {
    setEditingSession(session);
    
    // Parse weeklyDays from the database (comma-separated string)
    let weeklyDaysArray = [];
    if (session.weeklyDays) {
      weeklyDaysArray = session.weeklyDays.split(',').map(day => day.trim());
    } else if (session.type === 0 && session.weeklyDay !== null) {
      // Fallback to single day if WeeklyDays is not available
      weeklyDaysArray = [getDayName(session.weeklyDay)];
    }
    
    setFormData({
      name: session.name || '',
      stable: session.stable || '1',
      date: session.date.split('T')[0],
      startTime: session.startTime.substring(0, 5),
      endTime: session.endTime.substring(0, 5),
      level: getLevelName(session.level),
      type: getTypeName(session.type),
      target: getTargetName(session.target),
      maxParticipants: session.maxParticipants || 10,
      weeklyDays: weeklyDaysArray,
      weeklyDay: weeklyDaysArray.length > 0 ? weeklyDaysArray[0] : '',
      selectedHorses: []
    });
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      stable: '',
      target: '',
      level: '',
      type: '',
      weeklyDays: [],
      date: '',
      startTime: '',
      endTime: '',
      maxParticipants: 10,
      selectedHorses: []
    });
    setCurrentStep(1);
    setEditingSession(null);
  };

  // Participant management functions
  const handleManageParticipants = async (session) => {
    setSelectedSession(session);
    setSelectedUsers([]);
    setShowParticipantsModal(true);
    await fetchAvailableUsers();
  };

  // Filter users based on session target
  const getTargetUsers = () => {
    if (!selectedSession || !availableUsers) return [];
    
    
    const targetRole = selectedSession.target === 0 ? 0 : 1; // 0 = Rider, 1 = Owner
    
    return availableUsers.filter(user => {
      // Check if user is already assigned
      const isAlreadyAssigned = selectedSession.participants?.some(p => p.user.id === user.id);
      if (isAlreadyAssigned) return false;
      
      
      // For riders, filter by level (only for rider sessions)
      if (user.role === 0 && selectedSession.target === 0) { // Rider and rider session
        // For regular riders, check level match
        const sessionLevel = selectedSession.level ?? 0; // Default to Beginner if null
        const userLevel = user.level ?? 0; // Default to Beginner if null
        
        // Map session level to user level
        // Session levels: 0=Beginner, 1=Intermediate, 2=Professional
        // User levels: 0=Beginner, 1=Intermediate, 2=Professional
        return userLevel === sessionLevel;
      }
      
      // Exclude temporary users from riders section (they have their own section)
      if (user.role === 5) return false;
      
      // For riders in non-rider sessions, or riders that don't match level, exclude them
      if (user.role === 0) return false;
      
      // For owners, no level filtering needed
      return user.role === targetRole;
    });
  };

  // Filter coaches
  const getCoaches = () => {
    if (!selectedSession || !availableUsers) return [];
    
    const coachRole = 2; // Coach role
    
    return availableUsers.filter(user => 
      user.role === coachRole && 
      !selectedSession.participants?.some(p => p.user.id === user.id)
    );
  };

  // Filter temporary users
  const getTemporaryUsers = () => {
    if (!selectedSession || !availableUsers) return [];
    
    const temporaryRole = 5; // Temporary user role
    
    return availableUsers.filter(user => 
      user.role === temporaryRole && 
      !selectedSession.participants?.some(p => p.user.id === user.id)
    );
  };

  // Get participant count excluding coaches
  const getParticipantCount = (session) => {
    if (!session.participants) return 0;
    
    // Count only non-coach participants (exclude role 2 = Coach)
    return session.participants.filter(participant => participant.user.role !== 2).length;
  };

  // Get coaches in current session
  const getCoachesInSession = () => {
    if (!selectedSession?.participants) return [];
    
    return selectedSession.participants.filter(participant => participant.user.role === 2);
  };

  // Get participants in current session (excluding coaches)
  const getParticipantsInSession = () => {
    if (!selectedSession?.participants) return [];
    
    return selectedSession.participants.filter(participant => participant.user.role !== 2);
  };

  // Handle user selection
  const handleUserSelect = (user) => {
    setSelectedUsers(prev => {
      const isSelected = prev.some(u => u.id === user.id);
      if (isSelected) {
        return prev.filter(u => u.id !== user.id);
      } else {
        return [...prev, user];
      }
    });
  };

  // Handle confirmation
  const handleConfirmAssignments = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }

    // Check if session has enough capacity for all selected users (excluding coaches)
    const currentParticipants = getParticipantsInSession().length;
    const maxParticipants = selectedSession.maxParticipants;
    
    // Count only non-coach selected users for capacity validation
    const selectedParticipants = selectedUsers.filter(user => user.role !== 2); // Exclude coaches
    const selectedCoaches = selectedUsers.filter(user => user.role === 2); // Only coaches
    
    if (maxParticipants && (currentParticipants + selectedParticipants.length) > maxParticipants) {
      const availableSpots = maxParticipants - currentParticipants;
      toast.error(`Session capacity exceeded! Only ${availableSpots} spot(s) available, but ${selectedParticipants.length} participant(s) selected.`);
      return;
    }

    try {
      // Assign all selected users
      for (const user of selectedUsers) {
        await handleAssignParticipant(user.id);
      }
      
      setShowConfirmModal(false);
      setSelectedUsers([]);
      
      // Create a more detailed success message
      const participantCount = selectedParticipants.length;
      const coachCount = selectedCoaches.length;
      
      if (participantCount > 0 && coachCount > 0) {
        toast.success(`Successfully assigned ${participantCount} participant(s) and ${coachCount} coach(es)`);
      } else if (participantCount > 0) {
        toast.success(`Successfully assigned ${participantCount} participant(s)`);
      } else if (coachCount > 0) {
        toast.success(`Successfully assigned ${coachCount} coach(es)`);
      } else {
        toast.success(`Successfully assigned ${selectedUsers.length} user(s)`);
      }
    } catch (error) {
      console.error('Error assigning participants:', error);
      toast.error('Failed to assign some participants');
    }
  };

  const fetchAvailableUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users?limit=100`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      // The API returns { users: [...] } format
      const users = response.data?.users || [];
      setAvailableUsers(Array.isArray(users) ? users : []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
      setAvailableUsers([]); // Set empty array on error
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAssignParticipant = async (userId) => {
    if (!selectedSession) return;
    
    // Check if session has reached maximum capacity
    const currentParticipants = getParticipantsInSession().length;
    const maxParticipants = selectedSession.maxParticipants;
    
    if (maxParticipants && currentParticipants >= maxParticipants) {
      toast.error(`Session is full! Maximum capacity is ${maxParticipants} participants.`);
      return;
    }
    
    try {
      await axios.post(createApiUrlWithPrefix(`simplifiedsessions/${selectedSession.id}/participants`), {
        userId: userId
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      toast.success('Participant assigned successfully!');
      
      // Fetch updated sessions and update selectedSession
      const response = await axios.get(`${API_BASE_URL}/api/simplifiedsessions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Extract sessions from the paginated response
      const sessionsData = response.data.sessions || response.data;
      
      // Filter sessions based on view mode
      const filteredSessions = sessionsData.filter(session => {
        if (viewMode === 'weekly') return session.type === 0;
        if (viewMode === 'daily') return session.type === 1;
        return true;
      });
      
      setSessions(filteredSessions);
      
      // Update the selectedSession with the new participant data
      const updatedSession = filteredSessions.find(s => s.id === selectedSession.id);
      if (updatedSession) {
        setSelectedSession(updatedSession);
      }
    } catch (error) {
      console.error('Error assigning participant:', error);
      toast.error('Failed to assign participant');
    }
  };

  const handleRemoveParticipant = async (participantId) => {
    if (!selectedSession) return;
    
    try {
      await axios.delete(createApiUrlWithPrefix(`simplifiedsessions/${selectedSession.id}/participants/${participantId}`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      toast.success('Participant removed successfully!');
      
      // Fetch updated sessions and update selectedSession
      const response = await axios.get(`${API_BASE_URL}/api/simplifiedsessions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Extract sessions from the paginated response
      const sessionsData = response.data.sessions || response.data;
      
      // Filter sessions based on view mode
      const filteredSessions = sessionsData.filter(session => {
        if (viewMode === 'weekly') return session.type === 0;
        if (viewMode === 'daily') return session.type === 1;
        return true;
      });
      
      setSessions(filteredSessions);
      
      // Update the selectedSession with the updated participant data
      const updatedSession = filteredSessions.find(s => s.id === selectedSession.id);
      if (updatedSession) {
        setSelectedSession(updatedSession);
      }
    } catch (error) {
      console.error('Error removing participant:', error);
      toast.error('Failed to remove participant');
    }
  };

  // Horse management functions
  const handleManageHorses = async (session) => {
    console.log('Opening horse management for session:', session.id);
    setSelectedSession(session);
    setShowHorsesModal(true);
    // Fetch both session horses and all available horses
    await Promise.all([
      fetchSessionHorses(session.id),
      fetchHorses()
    ]);
    console.log('Horse management data loaded');
  };

  const fetchSessionHorses = async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(createApiUrlWithPrefix(`sessions/${sessionId}/horses`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      // Ensure we always set an array
      const sessionHorsesData = Array.isArray(response.data) ? response.data : [];
      setSessionHorses(sessionHorsesData);
      console.log('Fetched session horses:', sessionHorsesData.length, 'horses for session', sessionId);
    } catch (error) {
      console.error('Failed to fetch session horses:', error);
      setSessionHorses([]);
    }
  };

  const assignHorseToSession = async (sessionId, horseId) => {
    try {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      
      await axios.post(createApiUrlWithPrefix(`sessions/${sessionId}/assign-horse`), {
        horseId,
        assignedByUserId: userId ? parseInt(userId) : null
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      toast.success('Horse assigned to session successfully');
      await fetchSessionHorses(sessionId);
      await fetchSessions(); // Refresh the main session list
    } catch (error) {
      console.error('Failed to assign horse:', error);
      toast.error('Failed to assign horse to session');
    }
  };

  const removeHorseFromSession = async (sessionId, horseId) => {
    try {
      const token = localStorage.getItem('token');
      
      await axios.delete(createApiUrlWithPrefix(`sessions/${sessionId}/remove-horse/${horseId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      toast.success('Horse removed from session successfully');
      await fetchSessionHorses(sessionId);
      await fetchSessions(); // Refresh the main session list
    } catch (error) {
      console.error('Failed to remove horse:', error);
      toast.error('Failed to remove horse from session');
    }
  };

  const nextStep = () => {
    if (currentStep < 7) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleWeeklyDayToggle = (day) => {
    setFormData(prev => {
      const currentDays = prev.weeklyDays || [];
      const isSelected = currentDays.includes(day);
      const newDays = isSelected 
        ? currentDays.filter(d => d !== day)
        : [...currentDays, day];
      
      return {
        ...prev,
        weeklyDays: newDays
      };
    });
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'Beginner': return 'bg-green-100 text-green-800';
      case 'Intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'Professional': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Weekly': return 'bg-blue-100 text-blue-800';
      case 'Daily': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTargetColor = (target) => {
    switch (target) {
      case 'Rider': return 'bg-green-100 text-green-800';
      case 'Owner': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTime = (timeString) => {
    return timeString.substring(0, 5);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'} key={refreshKey}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('sessions.simplified.title')}</h1>
          <p className="mt-2 text-gray-600">
            {t('sessions.simplified.subtitle')}
          </p>
      </div>
        <div className="flex items-center space-x-3">
          <div className="flex bg-gray-100 rounded-lg p-1">
              <button
              onClick={() => setViewMode('weekly')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                viewMode === 'weekly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
              }`}
            >
              {t('sessions.simplified.weeklyView')}
              </button>
            <button
              onClick={() => setViewMode('daily')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                viewMode === 'daily' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
              }`}
            >
              {t('sessions.simplified.dailyView')}
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                viewMode === 'calendar' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
              }`}
            >
              {t('sessions.simplified.calendarView')}
            </button>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
            <Plus className="w-4 h-4 mr-2" />
            {t('sessions.simplified.createSession')}
            </button>
        </div>
          </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('common.search')}
            </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('sessions.searchPlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

          {/* Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('sessions.type')}
            </label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('common.all')}</option>
              <option value="Weekly">{t('sessions.simplified.form.weekly')}</option>
              <option value="Daily">{t('sessions.simplified.form.daily')}</option>
            </select>
          </div>

          {/* Target Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('sessions.target')}
            </label>
            <select
              value={targetFilter}
              onChange={(e) => setTargetFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('common.all')}</option>
              <option value="Rider">{t('sessions.simplified.form.riders')}</option>
              <option value="Owner">{t('sessions.simplified.form.owners')}</option>
            </select>
          </div>

          {/* Stable Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('sessions.stable')}
            </label>
            <select
              value={stableFilter}
              onChange={(e) => setStableFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('common.all')}</option>
              <option value="1">{t('sessions.simplified.form.stable1')}</option>
              <option value="2">{t('sessions.simplified.form.stable2')}</option>
              <option value="3">{t('sessions.simplified.form.stable3')}</option>
            </select>
          </div>
        </div>

        {/* Clear Filters */}
        <div className="mt-4 flex justify-end">
            <button
            onClick={() => {
              setSearchTerm('');
              setTypeFilter('');
              setTargetFilter('');
              setStableFilter('');
              setCurrentPage(1);
            }}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            {t('common.clearFilters')}
            </button>
        </div>
      </div>

      {/* Sessions List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {viewMode === 'weekly' ? 'Weekly Sessions' : 
             viewMode === 'daily' ? 'Daily Sessions' : 
             'Calendar View'} ({viewMode === 'calendar' ? 'All Sessions' : sessions.length})
          </h3>
          </div>
        
        {viewMode === 'calendar' ? (
          <CalendarView
            currentDate={currentDate}
            sessions={sessions}
            onNavigateMonth={navigateMonth}
            onNavigateWeek={navigateWeek}
            onNavigateDay={navigateDay}
            onGoToToday={goToToday}
            onEditSession={handleEdit}
            onManageParticipants={handleManageParticipants}
            onDeleteSession={handleDeleteSession}
            formatTime={formatTime}
            getLevelColor={getLevelColor}
            getTypeColor={getTypeColor}
            getTargetColor={getTargetColor}
          />
        ) : (
          <div className="divide-y divide-gray-200">
          {sessions.map((session) => (
            <div key={session.id} className="p-6 hover:bg-gray-50 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <h4 className="text-lg font-semibold text-gray-900">
                      {session.name || `${session.level} Level Session`}
                    </h4>
                    {session.stable && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {t(`sessions.simplified.form.stable${session.stable}`)}
                      </span>
                    )}
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getLevelColor(session.level)}`}>
                      {session.levelName || t(`sessions.simplified.form.${session.level?.toLowerCase()}`)}
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(session.type)}`}>
                      {session.typeName || t(`sessions.simplified.form.${session.type?.toLowerCase()}`)}
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTargetColor(session.target)}`}>
                      {session.targetName || t(`sessions.simplified.form.${session.target?.toLowerCase()}s`)}
                    </span>
                    {/* Remaining spots indicator */}
                    {session.maxParticipants && (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        session.remainingSpots > 0 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        <Users className="w-3 h-3 mr-1" />
                        {session.remainingSpots > 0 
                          ? `${session.remainingSpots} spots left` 
                          : 'Full'
                        }
                      </span>
                    )}
                    {session.type === 0 && session.weeklyDays && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-purple-200 text-purple-900 border border-purple-300">
                        📅 {session.weeklyDays.split(',').map(day => t(`sessions.daysOfWeek.${day.toLowerCase()}`)).join(', ')}
                      </span>
                    )}
                        </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span>{new Date(session.date).toLocaleDateString()}</span>
                      </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Clock className="w-4 h-4 mr-2" />
                      <span>{formatTime(session.startTime)} - {formatTime(session.endTime)}</span>
                      </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Users className="w-4 h-4 mr-2" />
                      <span>
                        {getParticipantCount(session)}
                        {session.maxParticipants ? `/${session.maxParticipants}` : ''} 
                        {t('sessions.participants')}
                      </span>
                      </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="text-lg mr-2">🐎</span>
                      <span>
                        {session.horses && session.horses.length > 0 
                          ? `${session.horses.length} horse(s) assigned`
                          : 'No horses assigned'
                        }
                      </span>
                    </div>
                      </div>
                  
                  {/* Assigned Horses */}
                  {session.horses && session.horses.length > 0 && (
                    <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center text-green-800 mb-2">
                        <span className="text-lg mr-2">🐎</span>
                        <span className="font-medium">Assigned Horses</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {session.horses.map((horse, index) => (
                          <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {horse.horseName}
                            {horse.riderName && ` (${horse.riderName})`}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Weekly session day display */}
                    {session.type === 0 && session.weeklyDays && (
                      <div className="mb-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                        <div className="flex items-center text-purple-800">
                          <Calendar className="w-5 h-5 mr-2" />
                          <span className="font-semibold">{t('sessions.types.weekly')} - {t('sessions.form.recurringEndDate')} {session.weeklyDays.split(',').map(day => t(`sessions.daysOfWeek.${day.trim().toLowerCase()}`)).join(', ')}</span>
                            </div>
                            </div>
                          )}
                  
                  {session.expiresAt && (
                    <div className="text-sm text-orange-600">
                      <Clock className="w-4 h-4 inline mr-1" />
                      Expires: {new Date(session.expiresAt).toLocaleDateString()}
                        </div>
                      )}
                        </div>
                
                <div className="flex flex-col space-y-2 ml-6">
                        <button
                    onClick={() => handleEdit(session)}
                    className="inline-flex items-center px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                        >
                    <Edit className="w-4 h-4 mr-1" />
                    {t('sessions.simplified.editSession')}
                        </button>
                        <button
                    onClick={() => handleManageParticipants(session)}
                    className="inline-flex items-center px-3 py-1.5 text-sm text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors"
                        >
                    <Users className="w-4 h-4 mr-1" />
                    {t('sessions.simplified.manageParticipants')}
                        </button>
                        <button
                    onClick={() => handleManageHorses(session)}
                    className="inline-flex items-center px-3 py-1.5 text-sm text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-md transition-colors"
                        >
                    <span className="text-lg mr-1">🐎</span>
                    Manage Horses
                        </button>
                        <button
                          onClick={() => handleDeleteSession(session.id)}
                    className="inline-flex items-center px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                        >
                    <Trash2 className="w-4 h-4 mr-1" />
                    {t('sessions.simplified.deleteSession')}
                        </button>
                      </div>
              </div>
            </div>
                ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              {t('common.showing')} {((currentPage - 1) * itemsPerPage) + 1} {t('common.to')} {Math.min(currentPage * itemsPerPage, totalItems)} {t('common.of')} {totalItems} {t('common.results')}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className={`px-3 py-1 text-sm rounded-md ${
                  currentPage === 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {t('common.first')}
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-3 py-1 text-sm rounded-md ${
                  currentPage === 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {t('common.previous')}
              </button>
              <span className="px-3 py-1 text-sm text-gray-700">
                {t('common.page')} {currentPage} {t('common.of')} {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 text-sm rounded-md ${
                  currentPage === totalPages
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {t('common.next')}
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 text-sm rounded-md ${
                  currentPage === totalPages
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {t('common.last')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* No Sessions */}
      {sessions.length === 0 && !loading && (
        <div className="text-center py-12">
          <Calendar className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No sessions found</h3>
          <p className="text-gray-500 mb-4">
            {viewMode === 'daily' 
              ? 'No daily sessions have been created yet.' 
              : 'No weekly sessions have been created yet.'}
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create First Session
          </button>
        </div>
      )}

      {/* Create Session Modal - Multi-Step Wizard */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowCreateModal(false)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
{editingSession ? t('sessions.editSession') : t('sessions.createNewSession')} - {t('common.step')} {currentStep} {t('common.of')} 7
                </h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

              {/* Progress Bar */}
              <div className="mb-8">
                <div className="flex items-center justify-between">
                  {[1, 2, 3, 4, 5, 6].map((step) => (
                    <div key={step} className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        step <= currentStep ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                      }`}>
                        {step}
                      </div>
                      {step < 7 && (
                        <div className={`w-16 h-1 mx-2 ${
                          step < currentStep ? 'bg-blue-600' : 'bg-gray-200'
                        }`} />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Step Content */}
              <div className="space-y-6">
                {/* Step 1: Session Name & Stable */}
                {currentStep === 1 && (
                <div className="space-y-4">
<h4 className="text-lg font-medium text-gray-900">{t('sessions.sessionDetails')}</h4>
                  <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
{t('sessions.sessionName')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
placeholder={t('sessions.sessionNamePlaceholder')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
{t('sessions.stable')} <span className="text-red-500">*</span>
                      </label>
                      <select
                        required
                        value={formData.stable}
                        onChange={(e) => setFormData({...formData, stable: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
<option value="">{t('sessions.selectStable')}</option>
                        {stables.map((stable) => (
                          <option key={stable.value} value={stable.value}>
                            {stable.label}
                          </option>
                        ))}
                      </select>
                  </div>
                </div>
                )}

                {/* Step 2: Target (Riders or Owners) */}
                {currentStep === 2 && (
                  <div className="space-y-4">
<h4 className="text-lg font-medium text-gray-900">{t('sessions.whoIsThisSessionFor')}</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {sessionTargets.map((target) => (
                  <button
                          key={target.value}
                    type="button"
                          onClick={() => setFormData({...formData, target: target.value})}
                          className={`p-4 border-2 rounded-lg text-center transition-colors ${
                            formData.target === target.value
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="text-2xl mb-2">
                            {target.value === 'Rider' ? '🏇' : '👥'}
                      </div>
                          <div className="font-medium">{target.label}</div>
                  </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 3: Rider Level (only if Riders selected) */}
                {currentStep === 3 && formData.target === 'Rider' && (
                  <div className="space-y-4">
                    <h4 className="text-lg font-medium text-gray-900">Rider Level</h4>
                    <div className="grid grid-cols-1 gap-4">
                      {riderLevels.map((level) => (
                  <button
                          key={level.value}
                    type="button"
                          onClick={() => setFormData({...formData, level: level.value})}
                          className={`p-4 border-2 rounded-lg text-left transition-colors ${
                            formData.level === level.value
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="font-medium">{level.label}</div>
                          <div className="text-sm text-gray-600">
                            {level.value === 'Beginner' && 'New to horseback riding'}
                            {level.value === 'Intermediate' && 'Some experience with horses'}
                            {level.value === 'Professional' && 'Advanced riding skills'}
                          </div>
                  </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 4: Session Type */}
                {currentStep === 4 && (
                  <div className="space-y-4">
                    <h4 className="text-lg font-medium text-gray-900">Session Type</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {sessionTypes.map((type) => (
                  <button
                          key={type.value}
                    type="button"
                          onClick={() => setFormData({...formData, type: type.value})}
                          className={`p-4 border-2 rounded-lg text-center transition-colors ${
                            formData.type === type.value
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="text-2xl mb-2">
                            {type.value === 'Weekly' ? '📅' : '📆'}
                      </div>
                          <div className="font-medium">{type.label}</div>
                          <div className="text-sm text-gray-600">
                            {type.value === 'Weekly' ? 'Repeats every week' : 'One-time session'}
                      </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 5: Weekly Days or Date Selection */}
                {currentStep === 5 && (
                  <div className="space-y-4">
                    {formData.type === 'Weekly' ? (
                      <>
                        <h4 className="text-lg font-medium text-gray-900">Select Days of the Week</h4>
                        <p className="text-sm text-gray-600">Choose which days this session should repeat</p>
                        <p className="text-sm text-blue-600 font-medium">
                          Selected: {(formData.weeklyDays || []).length} day(s) - {(formData.weeklyDays || []).join(', ') || 'None'}
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          {weeklyDays.map((day) => (
                            <button
                              key={day.value}
                              type="button"
                              onClick={() => handleWeeklyDayToggle(day.value)}
                    className={`p-3 border-2 rounded-lg text-center transition-colors ${
                                (formData.weeklyDays || []).includes(day.value)
                                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              {day.label}
                  </button>
                          ))}
                </div>
                      </>
                    ) : (
                      <>
                        <h4 className="text-lg font-medium text-gray-900">Select Date</h4>
                        <p className="text-sm text-gray-600">Choose the date for this one-time session</p>
                            <input
                          type="date"
                          required
                          value={formData.date}
                          onChange={(e) => setFormData({...formData, date: e.target.value})}
                          min={new Date().toISOString().split('T')[0]}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </>
                    )}
              </div>
                )}

                {/* Step 6: Time Selection & Capacity */}
                {currentStep === 6 && (
                <div className="space-y-4">
                    <h4 className="text-lg font-medium text-gray-900">Time Selection & Capacity</h4>
                    <div className="grid grid-cols-2 gap-4">
                  <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Start Time <span className="text-red-500">*</span>
                    </label>
                    <input
                          type="time"
                      required
                          value={formData.startTime}
                          onChange={(e) => setFormData({...formData, startTime: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          End Time <span className="text-red-500">*</span>
                            </label>
                    <input
                          type="time"
                          required
                          value={formData.endTime}
                          onChange={(e) => setFormData({...formData, endTime: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                    
                    {/* Max Participants */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Maximum Participants
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="50"
                          value={formData.maxParticipants}
                          onChange={(e) => setFormData({...formData, maxParticipants: parseInt(e.target.value) || 10})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="10"
                        />
                        <p className="text-sm text-gray-500 mt-1">
                          Set the maximum number of participants allowed in this session
                        </p>
                    </div>
                  </div>
                )}

                {/* Step 7: Horse Selection */}
                {currentStep === 7 && (
                  <div className="space-y-4">
                    <h4 className="text-lg font-medium text-gray-900">Select Horses</h4>
                    <p className="text-sm text-gray-600">Choose which horses will be available for this session (optional)</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-64 overflow-y-auto">
                      {horses.map((horse) => (
                        <div
                          key={horse.id}
                          onClick={() => {
                            const isSelected = (formData.selectedHorses || []).includes(horse.id);
                            if (isSelected) {
                              setFormData({
                                ...formData,
                                selectedHorses: (formData.selectedHorses || []).filter(id => id !== horse.id)
                              });
                            } else {
                              setFormData({
                                ...formData,
                                selectedHorses: [...(formData.selectedHorses || []), horse.id]
                              });
                            }
                          }}
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                            (formData.selectedHorses || []).includes(horse.id)
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <div className="text-2xl">🐎</div>
                            <div>
                              <div className="font-medium">{horse.name}</div>
                              <div className="text-sm text-gray-600">
                                {horse.breed && `${horse.breed} • `}
                                Owner: {horse.ownerName}
                              </div>
                              {horse.riderName && (
                                <div className="text-sm text-gray-500">
                                  Rider: {horse.riderName}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {horses.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <div className="text-4xl mb-2">🐎</div>
                        <p>No horses available</p>
                        <p className="text-sm">Add horses in the Horses management section</p>
                      </div>
                    )}
                    
                    <div className="text-sm text-blue-600 font-medium">
                      Selected: {formData.selectedHorses.length} horse(s)
                    </div>
                  </div>
                )}
              </div>


              {/* Navigation Buttons */}
              <div className="flex justify-between pt-6 border-t border-gray-200 mt-8">
                <button
                  type="button"
                  onClick={prevStep}
                  disabled={currentStep === 1}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    currentStep === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
{t('common.previous')}
                </button>
                
                <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
{t('common.cancel')}
                </button>
                  
                  {currentStep === 7 ? (
                <button
                      type="button"
                      onClick={editingSession ? handleEditSession : handleCreateSession}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      {editingSession ? 'Update Session' : `Create Session${formData.type === 'Weekly' && (formData.weeklyDays || []).length > 1 ? 's' : ''}`}
                </button>
                  ) : (
                      <button
                      type="button"
                      onClick={nextStep}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
{t('common.next')}
                      </button>
                  )}
                      </div>
                      </div>
                    </div>
                    </div>
                  </div>
                )}

      {/* Participants Management Modal */}
      {showParticipantsModal && selectedSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center space-x-3">
                <h3 className="text-xl font-semibold text-gray-800">Manage Participants</h3>
                <span className="text-sm text-gray-600 bg-blue-100 px-3 py-1 rounded-full">
                  {selectedSession.name}
                </span>
                      </div>
              <button
                onClick={() => setShowParticipantsModal(false)}
                className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-full"
              >
                <XCircle className="w-6 h-6" />
              </button>
                    </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Current Participants */}
              <div className="space-y-6">
                {/* Coaches Section */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-medium text-gray-800">Coaches</h4>
                    <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                      {getCoachesInSession().length} assigned
                    </span>
                  </div>
                  
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 min-h-[150px] max-h-[200px] overflow-y-auto">
                    {getCoachesInSession().length > 0 ? (
                      <div className="space-y-2">
                        {getCoachesInSession().map((participant) => (
                          <div key={participant.id} className="flex items-center justify-between p-3 bg-white rounded-md border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                                <span className="text-purple-600 font-medium text-sm">
                                  {participant.user.firstName.charAt(0)}{participant.user.lastName.charAt(0)}
                                </span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-900">{participant.user.firstName} {participant.user.lastName}</span>
                                <div className="text-sm text-gray-500">Coach</div>
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveParticipant(participant.id)}
                              className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-full transition-colors"
                              title={t('sessions.simplified.removeCoach')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
                          <Users className="w-6 h-6 text-gray-400" />
                        </div>
                        <p className="text-gray-500 text-sm">No coaches assigned</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Participants Section */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-medium text-gray-800">Participants</h4>
                    <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                      {getParticipantsInSession().length}
                      {selectedSession.maxParticipants ? `/${selectedSession.maxParticipants}` : ''} 
                      assigned
                    </span>
                  </div>
                  
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 min-h-[150px] max-h-[200px] overflow-y-auto">
                    {getParticipantsInSession().length > 0 ? (
                      <div className="space-y-2">
                        {getParticipantsInSession().map((participant) => (
                          <div key={participant.id} className="flex items-center justify-between p-3 bg-white rounded-md border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <span className="text-blue-600 font-medium text-sm">
                                  {participant.user.firstName.charAt(0)}{participant.user.lastName.charAt(0)}
                                </span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-900">{participant.user.firstName} {participant.user.lastName}</span>
                                <div className="text-sm text-gray-500">
                                  {participant.user.role === 0 ? 'Rider' : 
                                   participant.user.role === 1 ? 'Owner' : 
                                   participant.user.role === 5 ? 'Temporary User' : 
                                   'Other'}
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveParticipant(participant.id)}
                              className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-full transition-colors"
                              title={t('sessions.simplified.removeParticipant')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
                          <Users className="w-6 h-6 text-gray-400" />
                        </div>
                        <p className="text-gray-500 text-sm">No participants assigned yet</p>
                        <p className="text-gray-400 text-xs mt-1">Select participants from the right panel</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Available Users - Separated by Type */}
              <div className="space-y-6">
                {/* Selection Summary */}
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-medium text-gray-800">Add Participants</h4>
                  <div className="flex items-center space-x-2">
                    <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                      {getTargetUsers().length + getCoaches().length + getTemporaryUsers().length} available
                    </span>
                    {selectedUsers.length > 0 && (
                      <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                        {selectedUsers.length} selected
                      </span>
                    )}
                    {selectedSession.maxParticipants && (
                      <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${
                        getParticipantsInSession().length >= selectedSession.maxParticipants
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {getParticipantsInSession().length}/{selectedSession.maxParticipants} capacity
                      </span>
                    )}
                    </div>
                      </div>

                {/* Capacity Warning */}
                {selectedSession.maxParticipants && getParticipantsInSession().length >= selectedSession.maxParticipants && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">
                          Session is Full
                        </h3>
                        <div className="mt-2 text-sm text-red-700">
                          <p>This session has reached its maximum capacity of {selectedSession.maxParticipants} participants. You cannot assign more participants.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Target Users Section */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h5 className="text-md font-medium text-gray-700">
                      {selectedSession.target === 0 
                        ? `Riders (${selectedSession.levelName || 'Beginner'})` 
                        : 'Owners'
                      }
                    </h5>
                    <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full">
                      {getTargetUsers().length} available
                    </span>
                      </div>
                  
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 min-h-[200px] max-h-[300px] overflow-y-auto">
                    {loadingUsers ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-3"></div>
                        <p className="text-gray-500 text-sm">Loading users...</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {getTargetUsers().map((user) => {
                          const isSelected = selectedUsers.some(u => u.id === user.id);
                          return (
                            <div 
                              key={user.id} 
                              className={`flex items-center justify-between p-3 rounded-md border transition-all cursor-pointer ${
                                isSelected 
                                  ? 'bg-green-50 border-green-300 shadow-md' 
                                  : 'bg-white border-gray-200 shadow-sm hover:shadow-md'
                              }`}
                              onClick={() => handleUserSelect(user)}
                            >
                              <div className="flex items-center space-x-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                  isSelected ? 'bg-green-100' : 'bg-blue-100'
                                }`}>
                                  <span className={`font-medium text-sm ${
                                    isSelected ? 'text-green-600' : 'text-blue-600'
                                  }`}>
                                    {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                                  </span>
                    </div>
                    <div>
                                  <span className={`font-medium ${
                                    isSelected ? 'text-green-900' : 'text-gray-900'
                                  }`}>
                                    {user.firstName} {user.lastName}
                                  </span>
                                  <div className="text-sm text-gray-500">
                                    {selectedSession.target === 0 ? 'Rider' : 'Owner'}
                    </div>
                  </div>
                              </div>
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                isSelected 
                                  ? 'bg-green-500 border-green-500' 
                                  : 'border-gray-300'
                              }`}>
                                {isSelected && (
                                  <CheckCircle className="w-3 h-3 text-white" />
                                )}
                              </div>
                            </div>
                          );
                        })}
                        {getTargetUsers().length === 0 && (
                          <div className="text-center py-8">
                            <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
                              <Users className="w-6 h-6 text-gray-400" />
                            </div>
                            <p className="text-gray-500 text-sm">
                              No {selectedSession.target === 0 ? 'riders' : 'owners'} available
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
              </div>

                {/* Coaches Section */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h5 className="text-md font-medium text-gray-700">Coaches</h5>
                    <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2 py-0.5 rounded-full">
                      {getCoaches().length} available
                    </span>
                  </div>
                  
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 min-h-[200px] max-h-[300px] overflow-y-auto">
                {loadingUsers ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto mb-3"></div>
                        <p className="text-gray-500 text-sm">Loading coaches...</p>
                  </div>
                ) : (
                      <div className="space-y-2">
                        {getCoaches().map((user) => {
                          const isSelected = selectedUsers.some(u => u.id === user.id);
                          return (
                            <div 
                              key={user.id} 
                              className={`flex items-center justify-between p-3 rounded-md border transition-all cursor-pointer ${
                                isSelected 
                                  ? 'bg-green-50 border-green-300 shadow-md' 
                                  : 'bg-white border-gray-200 shadow-sm hover:shadow-md'
                              }`}
                              onClick={() => handleUserSelect(user)}
                            >
                              <div className="flex items-center space-x-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                  isSelected ? 'bg-green-100' : 'bg-purple-100'
                                }`}>
                                  <span className={`font-medium text-sm ${
                                    isSelected ? 'text-green-600' : 'text-purple-600'
                                  }`}>
                                    {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                                  </span>
                    </div>
                    <div>
                                  <span className={`font-medium ${
                                    isSelected ? 'text-green-900' : 'text-gray-900'
                                  }`}>
                                    {user.firstName} {user.lastName}
                            </span>
                                  <div className="text-sm text-gray-500">Coach</div>
                      </div>
                    </div>
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                isSelected 
                                  ? 'bg-green-500 border-green-500' 
                                  : 'border-gray-300'
                              }`}>
                                {isSelected && (
                                  <CheckCircle className="w-3 h-3 text-white" />
                            )}
                          </div>
                      </div>
                          );
                        })}
                        {getCoaches().length === 0 && (
                          <div className="text-center py-8">
                            <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
                              <UserPlus className="w-6 h-6 text-gray-400" />
                    </div>
                            <p className="text-gray-500 text-sm">No coaches available</p>
                  </div>
                )}
                      </div>
                    )}
                  </div>
              </div>

                {/* Temporary Users Section */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h5 className="text-md font-medium text-gray-700">Temporary Users</h5>
                    <span className="bg-orange-100 text-orange-800 text-xs font-medium px-2 py-0.5 rounded-full">
                      {getTemporaryUsers().length} available
                    </span>
                  </div>
                  
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 min-h-[200px] max-h-[300px] overflow-y-auto">
                    {loadingUsers ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600 mx-auto mb-3"></div>
                        <p className="text-gray-500 text-sm">Loading temporary users...</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {getTemporaryUsers().map((user) => {
                          const isSelected = selectedUsers.some(u => u.id === user.id);
                          return (
                            <div 
                              key={user.id} 
                              className={`flex items-center justify-between p-3 rounded-md border transition-all cursor-pointer ${
                                isSelected 
                                  ? 'bg-green-50 border-green-300 shadow-md' 
                                  : 'bg-white border-gray-200 shadow-sm hover:shadow-md'
                              }`}
                              onClick={() => handleUserSelect(user)}
                            >
                              <div className="flex items-center space-x-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                  isSelected ? 'bg-green-100' : 'bg-orange-100'
                                }`}>
                                  <span className={`font-medium text-sm ${
                                    isSelected ? 'text-green-600' : 'text-orange-600'
                                  }`}>
                                    {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                                  </span>
                                </div>
                                <div>
                                  <span className={`font-medium ${
                                    isSelected ? 'text-green-900' : 'text-gray-900'
                                  }`}>
                                    {user.firstName} {user.lastName}
                                  </span>
                                  <div className="text-sm text-gray-500">Temporary User</div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                {isSelected ? (
                                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                                    <Check className="w-4 h-4 text-white" />
                                  </div>
                                ) : (
                                  <div className="w-6 h-6 border-2 border-gray-300 rounded-full"></div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        {getTemporaryUsers().length === 0 && (
                          <div className="text-center py-8">
                            <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
                              <UserPlus className="w-6 h-6 text-gray-400" />
                            </div>
                            <p className="text-gray-500 text-sm">No temporary users available</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                {selectedUsers.length > 0 && (
                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                      onClick={() => setSelectedUsers([])}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                      Clear Selection
                </button>
                <button
                      onClick={() => setShowConfirmModal(true)}
                      className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                      Assign Selected ({selectedUsers.length})
                </button>
              </div>
                )}
              </div>
            </div>

            <div className="mt-8 flex justify-end space-x-3">
              <button
                onClick={() => setShowParticipantsModal(false)}
                className="px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Horse Management Modal */}
      {showHorsesModal && selectedSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center space-x-3">
                <h3 className="text-xl font-semibold text-gray-800 flex items-center">
                  <span className="text-2xl mr-2">🐎</span>
                  Manage Horses
                </h3>
                <span className="text-sm text-gray-600 bg-purple-100 px-3 py-1 rounded-full">
                  {selectedSession.name}
                </span>
              </div>
              <button
                onClick={() => setShowHorsesModal(false)}
                className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-full"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Current Session Horses */}
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <span className="text-xl mr-2">🐎</span>
                    Assigned Horses ({Array.isArray(sessionHorses) ? sessionHorses.length : 0})
                  </h4>
                  
                  {!Array.isArray(sessionHorses) || sessionHorses.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                      <div className="text-4xl mb-2">🐎</div>
                      <p>No horses assigned to this session</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {Array.isArray(sessionHorses) && sessionHorses.map((horse) => (
                        <div key={horse.id} className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="text-2xl">🐎</div>
                            <div>
                              <div className="font-medium text-gray-900">{horse.horseName}</div>
                              <div className="text-sm text-gray-600">
                                {horse.horseBreed && `${horse.horseBreed} • `}
                                Owner: {horse.ownerName}
                              </div>
                              {horse.riderName && (
                                <div className="text-sm text-gray-500">
                                  Rider: {horse.riderName}
                                </div>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => removeHorseFromSession(selectedSession.id, horse.horseId)}
                            className="text-red-600 hover:text-red-800 p-2 hover:bg-red-100 rounded-lg transition-colors"
                            title="Remove horse from session"
                          >
                            <XCircle className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Available Horses to Assign */}
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <span className="text-xl mr-2">➕</span>
                    Available Horses ({Array.isArray(horses) && Array.isArray(sessionHorses) ? horses.filter(h => !sessionHorses.some(sh => sh.horseId === h.id)).length : 0})
                  </h4>
                  
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {Array.isArray(horses) && Array.isArray(sessionHorses) && horses
                      .filter(horse => !sessionHorses.some(sh => sh.horseId === horse.id))
                      .map((horse) => (
                        <div key={horse.id} className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors">
                          <div className="flex items-center space-x-3">
                            <div className="text-2xl">🐎</div>
                            <div>
                              <div className="font-medium text-gray-900">{horse.name}</div>
                              <div className="text-sm text-gray-600">
                                {horse.breed && `${horse.breed} • `}
                                Owner: {horse.ownerName}
                              </div>
                              {horse.riderName && (
                                <div className="text-sm text-gray-500">
                                  Rider: {horse.riderName}
                                </div>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => assignHorseToSession(selectedSession.id, horse.id)}
                            className="text-green-600 hover:text-green-800 p-2 hover:bg-green-100 rounded-lg transition-colors"
                            title="Assign horse to session"
                          >
                            <CheckCircle className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                  </div>
                  
                  {Array.isArray(horses) && Array.isArray(sessionHorses) && horses.filter(h => !sessionHorses.some(sh => sh.horseId === h.id)).length === 0 && (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                      <div className="text-4xl mb-2">🐎</div>
                      <p>All horses are already assigned</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowHorsesModal(false)}
                className="px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Confirm Assignment</h3>
              <button
                onClick={() => setShowConfirmModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                Are you sure you want to assign {selectedUsers.length} user(s) to this session?
                {selectedUsers.some(user => user.role === 2) && selectedUsers.some(user => user.role !== 2) && (
                  <span className="block text-sm text-gray-500 mt-1">
                    (Coaches don't count toward participant capacity)
                  </span>
                )}
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4 max-h-40 overflow-y-auto">
                <div className="space-y-2">
                  {selectedUsers.map((user) => (
                    <div key={user.id} className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-green-600 font-medium text-xs">
                          {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-900">{user.firstName} {user.lastName}</span>
                        <span className="text-sm text-gray-500 ml-2">
                          ({user.role === 0 ? 'Rider' : user.role === 1 ? 'Owner' : user.role === 2 ? 'Coach' : user.role === 3 ? 'Admin' : 'SuperAdmin'})
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
                      <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
                      </button>
                      <button
                onClick={handleConfirmAssignments}
                className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                Confirm Assignment
                      </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SimplifiedSessionManagement;
